import { Text, Divider, Button, Stack, Box } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { getFormattedDate } from "@/utils/get-formatted-date"
import { ResourceObjectInsurance } from "../types"
import { DetailsCard } from "@/routes/app/-components/details-card"
import { ColumnLayout } from "@/routes/app/-components/column-layout"
import {
  AddResourceObjectActionIcon,
  EditResourceObjectInsuranceActionIcon,
} from "./resource-object-action-icons"
import { LabeledValue } from "@components/default-list/labeled-value"
import { PersonName } from "@components/PersonName"

interface InsuranceTabProps {
  activePrivateInsurances: ResourceObjectInsurance[]
  activeBusinessInsurances: ResourceObjectInsurance[]
  historyPrivateInsurances: ResourceObjectInsurance[]
  historyBusinessInsurances: ResourceObjectInsurance[]
  onEditInsurance: (insurance: ResourceObjectInsurance) => void
  onCreatePrivateInsurance: () => void
  onCreateBusinessInsurance: () => void
}

export function InsuranceTab({
  activePrivateInsurances,
  activeBusinessInsurances,
  historyPrivateInsurances,
  historyBusinessInsurances,
  onEditInsurance,
  onCreatePrivateInsurance,
  onCreateBusinessInsurance,
}: InsuranceTabProps) {
  const { t } = useTranslation("app_server")

  const renderInsuranceItem = (
    insurance: ResourceObjectInsurance,
    onEdit: (i: ResourceObjectInsurance) => void
  ) => (
    <Stack
      key={insurance.resourceObjectInsuranceId}
      style={{
        position: "relative",
        paddingRight: "3rem",
      }}
    >
      {insurance.insuredPersonId && (
        <LabeledValue
          label={t("Owner")}
          value={<PersonName personId={insurance.insuredPersonId} />}
        />
      )}
      {insurance.startDate && (
        <LabeledValue
          label={t("ValidSince")}
          value={getFormattedDate(insurance.startDate)}
        />
      )}
      {insurance.endDate && (
        <LabeledValue
          label={t("ValidTo")}
          value={getFormattedDate(insurance.endDate)}
        />
      )}
      <Box
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          display: "flex",
          gap: "0.5rem",
        }}
      >
        <EditResourceObjectInsuranceActionIcon
          onClick={() => onEdit(insurance)}
        />
      </Box>
      <Divider />
    </Stack>
  )

  return (
    <ColumnLayout>
      {activePrivateInsurances.length > 0 ? (
        <DetailsCard
          title={t("ActivePrivateInsurance")}
          buttons={
            <AddResourceObjectActionIcon onClick={onCreatePrivateInsurance} />
          }
        >
          {activePrivateInsurances.map((insurance) =>
            renderInsuranceItem(insurance, onEditInsurance)
          )}
        </DetailsCard>
      ) : (
        <DetailsCard
          title={t("ActivePrivateInsurance")}
          buttons={
            <AddResourceObjectActionIcon onClick={onCreatePrivateInsurance} />
          }
        >
          <Text color="dimmed">{t("NoData")}</Text>
        </DetailsCard>
      )}

      {activeBusinessInsurances.length > 0 ? (
        <DetailsCard
          title={t("ActiveBusinessInsurance")}
          buttons={
            <AddResourceObjectActionIcon onClick={onCreateBusinessInsurance} />
          }
        >
          {activeBusinessInsurances.map((insurance) =>
            renderInsuranceItem(insurance, onEditInsurance)
          )}
        </DetailsCard>
      ) : (
        <DetailsCard
          title={t("ActiveBusinessInsurance")}
          buttons={
            <AddResourceObjectActionIcon onClick={onCreateBusinessInsurance} />
          }
        >
          <Text color="dimmed">{t("NoData")}</Text>
        </DetailsCard>
      )}

      {historyPrivateInsurances.length > 0 && (
        <DetailsCard title={t("PrivateInsuranceHistory")}>
          {historyPrivateInsurances.map((insurance, i) => (
            <Stack
              key={insurance.resourceObjectInsuranceId}
              style={{
                marginBottom:
                  i < historyPrivateInsurances.length - 1 ? "1rem" : 0,
              }}
            >
              {insurance.insuredPersonId && (
                <LabeledValue
                  label={t("Owner")}
                  value={<PersonName personId={insurance.insuredPersonId} />}
                />
              )}
              {insurance.startDate && insurance.endDate && (
                <LabeledValue
                  label={t("ValidPeriod")}
                  value={
                    getFormattedDate(insurance.startDate) +
                    " - " +
                    getFormattedDate(insurance.endDate)
                  }
                />
              )}
              <Divider />
            </Stack>
          ))}
        </DetailsCard>
      )}

      {historyBusinessInsurances.length > 0 && (
        <DetailsCard title={t("BusinessInsuranceHistory")}>
          {historyBusinessInsurances.map((insurance, i) => (
            <Stack
              key={insurance.resourceObjectInsuranceId}
              style={{
                marginBottom:
                  i < historyBusinessInsurances.length - 1 ? "1rem" : 0,
              }}
            >
              {insurance.insuredPersonId && (
                <LabeledValue
                  label={t("Owner")}
                  value={<PersonName personId={insurance.insuredPersonId} />}
                />
              )}
              {insurance.startDate && insurance.endDate && (
                <LabeledValue
                  label={t("ValidPeriod")}
                  value={
                    getFormattedDate(insurance.startDate) +
                    " - " +
                    getFormattedDate(insurance.endDate)
                  }
                />
              )}
              <Divider />
            </Stack>
          ))}
        </DetailsCard>
      )}
    </ColumnLayout>
  )
}
